/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as Msdyn365 from '@msdyn365-commerce/core';
import { EmailRegex, updateProfileOneRF, UpdateProfileOneRFInput } from '@msdyn365-commerce-modules/retail-actions';
import { getTelemetryObject, IModuleProps, INodeProps, ITelemetryContent, Modal, ModalBody } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import { observable } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';

import { IAccountProfileEditConfig, IAccountProfileEditProps } from './account-profile-edit.props.autogenerated';
import AccountProfileEditButtonComponent from './components/account-profile-edit-button';
import AccountProfileEditErrorComponent from './components/account-profile-edit-error';
import AccountProfileEditInputComponent from './components/account-profile-edit-input';
import AccountProfileEditLabelComponent from './components/account-profile-edit-label';
import AccountProfileEditLoadingIconComponent from './components/account-profile-edit-loading-icon';
import AccountProfileEditLoadingMessageComponent from './components/account-profile-edit-loading-message';

export interface IAccountProfileEditViewState {
    isShowLoading: boolean;
}

export interface IAccountProfileEditItem {
    wrapper: INodeProps;
    key: string;
    label: React.ReactNode;
    errorMessage: React.ReactNode;
    input: React.ReactNode;
}

export interface IAccountProfileEditLocalAccount {
    localAccount: INodeProps;
    items: IAccountProfileEditItem[];
    errorMessage: React.ReactNode[];
    buttonsWrapper: INodeProps;
    buttons: React.ReactNode[];
    verificationSuccessModal: INodeProps;
    verificationSuccessModalMessage: INodeProps;
}

export interface IAccountProfileEditLoading {
    modal: INodeProps;
    modalBody: INodeProps;
    icon: React.ReactNode;
    message: React.ReactNode;
}

export interface IAccountProfileEditViewProps {
    className: string;
    viewState: IAccountProfileEditViewState;
    loading: IAccountProfileEditLoading;
    accountProfileEdit: IModuleProps;
    defaultAADConainer: INodeProps;
    aadConainer: INodeProps;
    heading: React.ReactNode;
    accountProfileEditLocalAccount: IAccountProfileEditLocalAccount;
    useEntraExternalId?: boolean; // making this optional for backward compatability
}

/**
 *
 * AccountProfileEdit component
 * All AAD related module is rendered on AAD page and we need to respect HTML contract provide by AAD.
 * Please ensure any change in module don't break contract with AAD.
 * @extends {React.Component<IAccountProfileEditProps<IAccountProfileEditConfig>>}
 */
@observer
class AccountProfileEdit extends React.Component<IAccountProfileEditProps<IAccountProfileEditConfig>> {
    private readonly moduleClassName: string = 'ms-account-profile-edit';

    private readonly telemetryContent: ITelemetryContent;

    @observable private givenName: string;

    @observable private surname: string;

    @observable private email: string;

    @observable private emailRegex: string;

    @observable private isInitialized: boolean;

    // OneRF state management for External Entra ID
    @observable private isUpdating: boolean;

    @observable private updateError: string | null;

    @observable private updateSuccess: boolean;

    private readonly customerAccountNumber: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private initializationTimer: any;

    constructor(props: IAccountProfileEditProps<IAccountProfileEditConfig>) {
        super(props);
        this.givenName = '';
        this.surname = '';
        this.email = '';
        this.emailRegex = EmailRegex.defaultRegex.source;
        this.isInitialized = false;

        // Initialize OneRF state for External Entra ID
        this.isUpdating = false;
        this.updateError = null;
        this.updateSuccess = false;

        // Get customer account number from context for cache key usage
        this.customerAccountNumber = this.props.context.request.user.customerAccountNumber || '';
        this.telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry
        );
    }

    public componentDidMount(): void {
        this._onInit();
    }

    // After successful AAD initialization, call initialize method provided by AAD to attach events.
    public componentDidUpdate(): void {
        // eslint-disable-next-line @typescript-eslint/dot-notation, @typescript-eslint/no-unsafe-member-access -- Auto-suppressed.
        if (this.isInitialized && window && window['$element'] && window['$element']['initialize']) {
            // eslint-disable-next-line @typescript-eslint/dot-notation, @typescript-eslint/no-unsafe-member-access -- Auto-suppressed.
            window['$element']['initialize']();
        }
    }

    public shouldComponentUpdate(
        nextProps: IAccountProfileEditProps<IAccountProfileEditConfig>,
        nextState: IAccountProfileEditViewState
    ): boolean {
        if (this.state === nextState && this.props.data === nextProps.data) {
            return false;
        }
        return true;
    }

    public render(): JSX.Element {
        const { config, resources } = this.props;

        const viewProps = {
            ...this.props,
            viewState: {
                isShowLoading: !this.isInitialized
            },
            accountProfileEdit: {
                moduleProps: this.props,
                className: classnames(this.moduleClassName, config.className)
            },
            loading: {
                modal: {
                    tag: Modal,
                    isOpen: true
                },
                modalBody: {
                    tag: ModalBody
                },
                icon: <AccountProfileEditLoadingIconComponent className={this.moduleClassName} />,
                message: <AccountProfileEditLoadingMessageComponent className={this.moduleClassName} message={resources.loadingMessage} />
            },
            defaultAADConainer: {
                id: 'api',
                style: { display: 'none' }
            },
            aadConainer: {
                id: this.isInitialized ? 'api' : null,
                className: `${this.moduleClassName}__container`
            },
            heading: config.heading && config.heading.text && (
                <Msdyn365.Text
                    className={`${this.moduleClassName}__heading`}
                    {...config.heading}
                    editProps={{ onEdit: this.handleHeadingChange, requestContext: this.props.context.request }}
                />
            ),
            // Conditional rendering: Use OneRF for External Entra ID, otherwise use AAD
            accountProfileEditLocalAccount: this._useEntraExternalId() ? this._renderEntraExternalIdAccount() : this._renderLocalAccount(),
            useEntraExternalId: this._useEntraExternalId()
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    public handleHeadingChange = (event: Msdyn365.ContentEditableEvent) => (this.props.config.heading.text = event.target.value);

    public onTextBoxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const id = event.target.id;
        const value = event.target.value;

        switch (id.toLowerCase()) {
            case 'givenname': {
                this.givenName = value;
                break;
            }
            case 'surname': {
                this.surname = value;
                break;
            }
            case 'email': {
                this.emailRegex = value;
                break;
            }
            default:
        }
    };

    // eslint-disable-next-line @typescript-eslint/naming-convention
    public _onInit = () => {
        this._prePopulateData();
        this._updateErrorMessage();

        // Check if AAD initialization is complete. AAD do not provide any event to subscribe so we need to check variable set by AAD to check initialization status.
        this.initializationTimer = setInterval(() => {
            this._isInitializationSuccessful();
        }, 100);
        setTimeout(() => {
            clearInterval(this.initializationTimer);
        }, 10_000);
    };

    // After successful AAD initialization, remove waiting and preload any data, if needed.
    private readonly _isInitializationSuccessful = () => {
        // eslint-disable-next-line @typescript-eslint/dot-notation, @typescript-eslint/no-unsafe-member-access -- Auto-suppressed.
        if (window && window['$diags'] && window['$diags']['initializationSuccessful']) {
            clearInterval(this.initializationTimer);
            this.isInitialized = true;
        }
    };

    private readonly _prePopulateData = () => {
        const { resources } = this.props;
        const invalidEmailAddressError = resources.invalidEmailAddressError || 'Please enter a valid email address.';

        // eslint-disable-next-line @typescript-eslint/dot-notation, @typescript-eslint/no-unsafe-member-access -- Auto-suppressed.
        if (window['SA_FIELDS'] && window['SA_FIELDS']['AttributeFields']) {
            // eslint-disable-next-line @typescript-eslint/dot-notation, @typescript-eslint/no-unsafe-member-access -- Auto-suppressed.
            window['SA_FIELDS']['AttributeFields'].map(
                (obj: { ID: string; PRE: string; PAT: string; UX_INPUT_TYPE: string; PAT_DESC: string }) => {
                    const value = obj.PRE ? this._htmlDecode(obj.PRE) : '';
                    switch (obj.ID.toLowerCase()) {
                        case 'givenname': {
                            this.givenName = value;
                            break;
                        }
                        case 'surname': {
                            this.surname = value;
                            break;
                        }
                        case 'email': {
                            this.email = value;
                            this.emailRegex = obj.PAT;
                            obj.PAT_DESC = invalidEmailAddressError;
                            break;
                        }
                        default:
                    }
                }
            );
        }
    };

    private _htmlDecode(str: string): string {
        const e = document.createElement('div');

        e.innerHTML = str;
        return e.childNodes.length === 0 ? '' : e.childNodes[0].nodeValue || '';
    }

    private readonly _updateErrorMessage = () => {
        const { resources } = this.props;

        // eslint-disable-next-line @typescript-eslint/dot-notation -- Auto-suppressed.
        if (window && window['CONTENT']) {
            const message = {
                required_field: resources.requiredFieldMissingError,
                verifying_blurb: resources.loadingMessage
            };

            // eslint-disable-next-line @typescript-eslint/dot-notation -- Auto-suppressed.
            Object.assign(window['CONTENT'], message);
        }
    };

    private _renderLocalAccount(): IAccountProfileEditLocalAccount {
        const { config, resources } = this.props;
        const localAccount = {
            localAccount: {
                id: 'attributeList',
                className: `${this.moduleClassName}__account-items attr`
            },
            items: [
                this._renderInput('givenName', 'text', this.givenName, resources.firstNameLabelText, resources.firstNameMaxLength),
                this._renderInput('surname', 'text', this.surname, resources.lastNameLabelText, resources.lastNameMaxLength)
            ],
            errorMessage: [
                <AccountProfileEditErrorComponent
                    key='requiredFieldMissing'
                    id='requiredFieldMissing'
                    className={this.moduleClassName}
                    message={resources.requiredFieldMissingSummaryError}
                />,
                <AccountProfileEditErrorComponent
                    key='fieldIncorrect'
                    id='fieldIncorrect'
                    className={this.moduleClassName}
                    message={resources.fieldIncorrectError}
                />,
                <AccountProfileEditErrorComponent
                    key='claimVerificationServerError'
                    id='claimVerificationServerError'
                    className={this.moduleClassName}
                />
            ],
            buttonsWrapper: {
                className: `${this.moduleClassName}__buttons`
            },
            buttons: [
                <AccountProfileEditButtonComponent
                    key='continue'
                    id='continue'
                    className={`${this.moduleClassName}__save-button`}
                    ariaLabel={resources.saveButtonAriaLabel}
                    text={resources.saveButtonText}
                    telemetryContent={this.telemetryContent}
                />,
                <AccountProfileEditButtonComponent
                    key='cancel'
                    id='cancel'
                    className={`${this.moduleClassName}__cancel-button`}
                    ariaLabel={resources.cancelButtonText}
                    text={resources.cancelButtonText}
                    telemetryContent={this.telemetryContent}
                />
            ],
            verificationSuccessModal: {
                className: `${this.moduleClassName}__verifying-modal verifying-modal`
            },
            verificationSuccessModalMessage: {
                id: 'verifying_blurb',
                className: `${this.moduleClassName}__verifying-modal-message`
            }
        };

        if (config.showEmail && this.email !== '') {
            localAccount.items.push(
                this._renderInput('email', 'email', this.email, resources.emailAddressLabelText, undefined, this.emailRegex)
            );
        }

        return localAccount;
    }

    private _renderInput(
        id: string,
        type: string,
        value: string,
        labelText: string,
        maxLength?: string,
        pattern?: string,
        disabled: boolean = false
    ): IAccountProfileEditItem {
        const className = `${this.moduleClassName}__account-item`;
        return {
            wrapper: {
                className: classnames(className, `${className}-${id}`, 'entry-item', 'attrEntry')
            },
            key: id,
            label: (
                <AccountProfileEditLabelComponent
                    {...{
                        id,
                        forId: id,
                        className,
                        text: labelText
                    }}
                />
            ),
            errorMessage: (
                <AccountProfileEditErrorComponent
                    {...{
                        className: this.moduleClassName,
                        type: 'item'
                    }}
                />
            ),
            input: (
                <AccountProfileEditInputComponent
                    {...{
                        id,
                        type,
                        value,
                        pattern,
                        className,
                        maxLength,
                        disabled,
                        onChange: this.onTextBoxChange
                    }}
                />
            )
        };
    }

    /**
     * Determines if External Entra ID is enabled to use OneRF instead of AAD
     * @returns {boolean} True if External Entra ID is enabled
     */
    private readonly _useEntraExternalId = (): boolean => {
        return this.props.context.request.features?.useEntraExternalId === true;
    };

    /**
     * Renders the External Entra ID account form using OneRF instead of AAD
     * @returns {IAccountProfileEditLocalAccount} Account form for External Entra ID
     */
    private _renderEntraExternalIdAccount(): IAccountProfileEditLocalAccount {
        const { config, resources } = this.props;
        const isAuthenticated = this.props.context.request.user.isAuthenticated;
        const localAccount = {
            localAccount: {
                id: 'entraExternalIdForm',
                className: `${this.moduleClassName}__account-items entra-external`
            },
            items: [
                this._renderInput(
                    'givenName',
                    'text',
                    this.givenName,
                    resources.firstNameLabelText,
                    resources.firstNameMaxLength,
                    undefined,
                    !isAuthenticated
                ),
                this._renderInput(
                    'surname',
                    'text',
                    this.surname,
                    resources.lastNameLabelText,
                    resources.lastNameMaxLength,
                    undefined,
                    !isAuthenticated
                )
            ],
            errorMessage: [
                <AccountProfileEditErrorComponent
                    key='requiredFieldMissing'
                    id='requiredFieldMissing'
                    className={this.moduleClassName}
                    message={resources.requiredFieldMissingSummaryError}
                />,
                <AccountProfileEditErrorComponent
                    key='fieldIncorrect'
                    id='fieldIncorrect'
                    className={this.moduleClassName}
                    message={resources.fieldIncorrectError}
                />,
                <AccountProfileEditErrorComponent
                    key='oneRFUpdateError'
                    id='oneRFUpdateError'
                    className={this.moduleClassName}
                    message={this.updateError || ''}
                />
            ],
            buttonsWrapper: {
                className: `${this.moduleClassName}__buttons`
            },
            buttons: [
                <AccountProfileEditButtonComponent
                    key='save'
                    id='save'
                    className={`${this.moduleClassName}__save-button ${this.isUpdating ? 'updating' : ''}`}
                    ariaLabel={resources.saveButtonAriaLabel}
                    text={this.isUpdating ? resources.loadingMessage : resources.saveButtonText}
                    telemetryContent={this.telemetryContent}
                    onClick={this._handleOneRFSave}
                    disabled={this.isUpdating || !isAuthenticated}
                />,
                <AccountProfileEditButtonComponent
                    key='cancel'
                    id='cancel'
                    className={`${this.moduleClassName}__cancel-button`}
                    ariaLabel={resources.cancelButtonText}
                    text={resources.cancelButtonText}
                    telemetryContent={this.telemetryContent}
                    onClick={this._handleCancel}
                />
            ],
            verificationSuccessModal: {
                className: `${this.moduleClassName}__verifying-modal verifying-modal ${this.updateSuccess ? 'success' : ''}`
            },
            verificationSuccessModalMessage: {
                id: 'update_success_message',
                className: `${this.moduleClassName}__verifying-modal-message`
            }
        };

        if (config.showEmail && this.email !== '') {
            localAccount.items.push(
                this._renderInput(
                    'email',
                    'email',
                    this.email,
                    resources.emailAddressLabelText,
                    undefined,
                    this.emailRegex,
                    !isAuthenticated
                )
            );
        }

        return localAccount;
    }

    /**
     * Handles saving profile changes using OneRF API for External Entra ID
     * @param event - The click event from the save button
     */
    private readonly _handleOneRFSave = async (event?: React.MouseEvent<HTMLElement>) => {
        // Prevent default form submission
        if (event) {
            event.preventDefault();
        }

        if (!this.props.context.request.user.isAuthenticated) {
            this.props.telemetry.warning('OneRF Profile Update attempted without authentication');
            return;
        }

        // Reset previous states
        this.updateError = null;
        this.updateSuccess = false;
        this.isUpdating = true;

        try {
            // Create input for OneRF data action with givenName, surname, email, and customer account number
            const oneRFInput = new UpdateProfileOneRFInput(
                this.customerAccountNumber,
                this.givenName || '',
                this.surname || '',
                this.props.config.showEmail ? this.email || '' : undefined
            );

            // Call OneRF profile update API
            const response = await updateProfileOneRF(oneRFInput, this.props.context.actionContext);

            if (response.success) {
                this.updateSuccess = true;
                this.isUpdating = false;

                // Redirect back to the calling page
                this._redirectBackToCaller();
            } else {
                // Handle update failure from OneRF response
                this.updateError = response.message || 'Profile update failed';
                this.updateSuccess = false;
                this.isUpdating = false;
                this.props.telemetry.error(`OneRF Profile Update Failed: ${this.updateError}`);
            }
        } catch (error) {
            this.updateError = error instanceof Error ? error.message : 'Unexpected error occurred';
            this.updateSuccess = false;
            this.isUpdating = false;
            this.props.telemetry.error(`OneRF Profile Update Unexpected Error: ${this.updateError}`);
        }
    };

    /**
     * Handles cancel action and redirects back to the calling page
     */
    private readonly _handleCancel = () => {
        this._redirectBackToCaller();
    };

    /**
     * Redirects back to the page that called this edit page
     * Falls back to /myaccount if no referrer is available
     */
    private readonly _redirectBackToCaller = () => {
        let referrer = document.referrer;

        if (referrer && referrer !== window.location.href) {
            // Fix URL if it's missing protocol
            if (referrer.startsWith('localhost:')) {
                referrer = `https://${referrer}`;
            }

            // Check if referrer is from the same domain for security
            try {
                const referrerUrl = new URL(referrer);
                const currentUrl = new URL(window.location.href);

                if (referrerUrl.origin === currentUrl.origin) {
                    this.props.telemetry.information(`Redirecting back to referrer: ${referrer}`);
                    window.location.href = referrer;
                    return;
                }
            } catch (error) {
                this.props.telemetry.warning(`Failed to parse referrer URL: ${error}. Redirecting to default page`);
                window.location.href = '/myprofile';
            }
        }

        // No valid referrer found - redirect to /myprofile page
        this.props.telemetry.information('No valid referrer found, redirecting to /myprofile');
        window.location.href = '/myprofile';
        return;
    };
}

export default AccountProfileEdit;
